// Phase 1+2 (ADR-0024) admin detail page for one adventure.
//
// Tabs:
//   - Goals      : list + create + edit + delete (Phase 2)
//   - Commitment : the editable form for commitment list (Phase 1)
//   - Metadata   : audit info, created_by, etc. (Phase 1)
//
// Phase 2 ships the Goals tab. Lifecycle buttons (Start/End) land
// in Phase 2-3; the current page is draft-only editable.
//
// The 'goals' tab is the highest-value tab for the operator's
// flow: "I just started a sprint, here are the goals we
// committed to". The 'commitment' tab handles the ticket list
// (already shipped in Phase 1); 'metadata' is the audit-trace
// surface.

import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, useEffect } from 'react';
import { adventuresApi } from './api';
import { BattleReportTab } from './BattleReportTab';
import type { AdventureLiveProgress } from './api';
import type { AdventureCommitmentTicket, AdventureGoal, AdventureWithGoals } from './types';
import { api } from '../../../lib/api';
import { ApiError } from '../../../lib/api';
import { elapsedFraction } from '../../../lib/campaign-clock';

function StatusBadge({ status }: { status: AdventureWithGoals['status'] }) {
  const palette: Record<AdventureWithGoals['status'], string> = {
    draft: 'bg-slate-700/40 text-slate-300 border-slate-600',
    active: 'bg-emerald-700/40 text-emerald-200 border-emerald-600',
    closed: 'bg-amber-700/40 text-amber-200 border-amber-600',
    archived: 'bg-zinc-700/40 text-zinc-400 border-zinc-600',
  };
  return (
    <span className={`inline-block px-2 py-0.5 text-xs rounded border ${palette[status]}`}>
      {status}
    </span>
  );
}

function toLocalInput(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function localToIso(local: string): string {
  return new Date(local).toISOString();
}

type Tab = 'goals' | 'report' | 'commitment' | 'sprint' | 'metadata';

export function AdventureDetailPage() {
  const { id = '' } = useParams<{ id: string }>();
  const qc = useQueryClient();
  const [tab, setTab] = useState<Tab>('goals');

  const detail = useQuery({
    queryKey: ['admin-adventure', id],
    queryFn: () => adventuresApi.get(id),
    enabled: !!id,
  });

  const teams = useQuery({
    queryKey: ['teams'],
    queryFn: () => api<{ teams: { id: string; name: string }[] }>('/api/teams'),
  });

  // Phase 3: lifecycle transitions. Start (draft → active) and
  // End (active → closed). On success we invalidate the adventure
  // so the new status + closed_summary render.
  const start = useMutation({
    mutationFn: () => adventuresApi.start(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['admin-adventure', id] }),
  });
  const end = useMutation({
    mutationFn: () => adventuresApi.end(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['admin-adventure', id] }),
  });

  // Phase 4: live progress — polled every 15s while the adventure
  // is active so the operator sees predictability + per-goal
  // progress tick up mid-sprint. Read-only.
  const isActive = detail.data?.adventure.status === 'active';
  const progress = useQuery({
    queryKey: ['admin-adventure-progress', id],
    queryFn: () => adventuresApi.progress(id),
    enabled: !!id && isActive,
    refetchInterval: isActive ? 15000 : false,
  });

  const teamName = teams.data?.teams.find((t) => t.id === detail.data?.adventure.team_id)?.name
    ?? detail.data?.adventure.team_id
    ?? '—';

  if (detail.isPending) return <div className="p-6 text-ink-muted">Loading…</div>;
  if (detail.isError) return (
    <div className="p-6 text-red-400">
      Failed to load adventure. {(detail.error as Error).message}
      <div className="mt-3"><Link to="/admin/adventures" className="text-brand hover:underline">← Back to list</Link></div>
    </div>
  );

  const adv = detail.data!.adventure;
  const isDraft = adv.status === 'draft';
  // Goals stay editable while active (the goal may change mid-sprint);
  // commitment is snapshotted at start so it stays draft-only.
  const canEditGoals = isDraft || adv.status === 'active';

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-semibold text-ink">{adv.name}</h1>
            <StatusBadge status={adv.status} />
          </div>
          <p className="text-sm text-ink-muted mt-1">
            {teamName} · created {new Date(adv.created_at).toLocaleString()}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {adv.status === 'draft' && (
            <button
              onClick={() => {
                if (!window.confirm('Start this adventure? The commitment is snapshotted and any active adventure for the team is closed.')) return;
                start.mutate();
              }}
              disabled={start.isPending}
              className="px-3 py-1.5 bg-brand text-white text-sm rounded hover:bg-brand-dark transition disabled:opacity-50"
            >
              {start.isPending ? 'Starting…' : 'Start adventure'}
            </button>
          )}
          {adv.status === 'active' && (
            <button
              onClick={() => {
                if (!window.confirm('End this adventure? Scores are computed and bonus coins are distributed now.')) return;
                end.mutate();
              }}
              disabled={end.isPending}
              className="px-3 py-1.5 bg-amber-700 text-white text-sm rounded hover:bg-amber-800 transition disabled:opacity-50"
            >
              {end.isPending ? 'Ending…' : 'End adventure'}
            </button>
          )}
          <Link to="/admin/adventures" className="text-sm text-ink-muted hover:text-ink">
            ← Back to list
          </Link>
        </div>
      </div>

      {!isDraft && (
        <div className="mb-4 px-3 py-2 bg-amber-900/30 border border-amber-700 text-amber-200 text-sm rounded">
          {adv.status === 'active' && (
            <>This adventure is <strong>active</strong>. It ends when you click End, when a new adventure for this team starts, or automatically after the sprint window closes.</>
          )}
          {adv.status === 'closed' && (
            <>
              This adventure is <strong>closed</strong>.
              {adv.closed_summary && (
                <span className="ml-2">Predictability <strong>{adv.closed_summary.predictability_pct}%</strong> · {adv.closed_summary.completed_count}/{adv.closed_summary.committed_count} committed tickets shipped.</span>
              )}
            </>
          )}
          {adv.status === 'archived' && (
            <>This adventure is <strong>archived</strong>. Read-only view.</>
          )}
        </div>
      )}

      {isActive && progress.data && (
        <LiveProgressPanel progress={progress.data} />
      )}

      {/* Tab nav. Native <button>s with role=tab + aria-selected;
          keyboard tab order matches visual order. The active tab
          has the brand underline; inactive tabs are muted. */}
      <div role="tablist" className="flex gap-1 mb-4 border-b border-border">
        {(['goals', 'report', 'commitment', 'sprint', 'metadata'] as const).map((t) => (
          <button
            key={t}
            role="tab"
            aria-selected={tab === t}
            onClick={() => setTab(t)}
            className={`px-4 py-2 text-sm capitalize border-b-2 -mb-px transition ${
              tab === t
                ? 'border-accent text-ink'
                : 'border-transparent text-ink-muted hover:text-ink'
            }`}
          >
            {t === 'goals' ? `Goals (${adv.goals.length})` : t === 'report' ? 'Battle report' : t}
          </button>
        ))}
      </div>

      {tab === 'goals' && (
        <GoalsTab
          adventureId={adv.id}
          canEdit={canEditGoals}
          goals={adv.goals}
          commitment={adv.commitment}
          hitNowIds={new Set((progress.data?.goals ?? []).filter((g) => g.hit_now).map((g) => g.id))}
        />
      )}
      {tab === 'report' && <BattleReportTab adventureId={adv.id} />}
      {tab === 'commitment' && (
        <CommitmentTab adventure={adv} isDraft={isDraft} onSaved={() => void qc.invalidateQueries({ queryKey: ['admin-adventure', id] })} />
      )}
      {tab === 'sprint' && (
        <SprintTab
          adventure={adv}
          onSaved={() => {
            void qc.invalidateQueries({ queryKey: ['admin-adventure', id] });
            void qc.invalidateQueries({ queryKey: ['admin-adventures'] });
          }}
        />
      )}
      {tab === 'metadata' && (
        <MetadataTab
          adventure={adv}
          onArchive={async () => {
            if (!window.confirm('Archive this adventure? The row stays in the database for the audit log but stops being editable.')) return;
            await adventuresApi.archive(adv.id);
            void qc.invalidateQueries({ queryKey: ['admin-adventure', id] });
            void qc.invalidateQueries({ queryKey: ['admin-adventures'] });
          }}
          onHardDelete={async () => {
            // Triple-confirm: hard delete is irreversible. The api
            // also refuses for non-terminal statuses, but the
            // dialog here protects against fat-finger deletes on
            // the wall TV kiosk where touches are imprecise.
            const name = adv.name;
            if (!window.confirm(`Permanently delete "${name}"? This cannot be undone.`)) return;
            if (!window.confirm(`Really delete "${name}"? Goals, bonuses, and the row itself will be removed. The audit log entry is preserved but the adventure is gone.`)) return;
            await adventuresApi.hardDelete(adv.id);
            void qc.invalidateQueries({ queryKey: ['admin-adventure', id] });
            void qc.invalidateQueries({ queryKey: ['admin-adventures'] });
            // After hard delete the detail page 404s — bounce to list.
            window.location.href = '/admin/adventures';
          }}
        />
      )}
    </div>
  );
}

// ---- GoalsTab (Phase 2) ----
//
// Lists goals + the create form. Edit (name + position) and
// delete are inline. The kind (tickets vs count) and shape
// fields can't change after creation — the operator deletes
// and recreates to switch shapes.

// ---- LiveProgressPanel (Phase 4) ----
//
// Shows the mid-sprint score for an active adventure: live
// predictability % (same formula as close, but window end =
// NOW), committed/completed counts, and per-goal progress.
// Polled by the parent every 15s. Read-only — this is the
// 'are we on track?' screen.

function LiveProgressPanel({ progress }: { progress: AdventureLiveProgress }) {
  const pct = progress.predictability_pct;
  const color = pct >= 80 ? 'text-accent-success' : pct >= 50 ? 'text-amber-300' : 'text-red-400';
  const daysLeft = Math.max(0, Math.ceil((new Date(progress.ends_at).getTime() - Date.now()) / 86400000));
  // Where the campaign is on its own clock, 0..1.
  //
  // This is the whole point of the marker: the bar above it says how much of
  // the COMMITMENT has shipped, and the tick says how much of the TIME has
  // gone. Read together they answer the question this screen exists for -
  // are we ahead of the clock or behind it - which today needs two separate
  // numbers to be compared by eye.
  const elapsed = elapsedFraction(progress.starts_at, progress.ends_at);
  const elapsedPct = Math.round(elapsed * 100);

  return (
    <div className="mb-4 bg-bg-elev border border-border rounded p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-medium text-ink">Live progress</h3>
        <span className="text-xs text-ink-muted">{daysLeft}d left</span>
      </div>
      <div className="flex items-baseline gap-3 mb-3">
        <span className={`text-3xl font-semibold ${color}`}>{pct}%</span>
        <span className="text-sm text-ink-muted">predictability</span>
        <span className="ml-auto text-sm text-ink-muted">
          {progress.completed_count}/{progress.committed_count} committed tickets shipped
        </span>
      </div>
      {/* The WRAPPER is relative and does NOT clip; the bar inside it does.
          That distinction is the whole reason the marker was invisible: a
          2px hairline inside an 8px-tall clipped bar is barely a mark at all,
          and any part of it that poked out was cut off. Drawn in the wrapper,
          it stands proud of the bar on both sides and reads as a mark.
          `h-2` on the bar plus py-1 on the wrapper gives the marker room. */}
      <div className="relative py-1">
        <div className="h-2 bg-bg-base rounded overflow-hidden">
          <div
            className={`h-full ${pct >= 80 ? 'bg-accent-success' : pct >= 50 ? 'bg-amber-400' : 'bg-red-500'} transition-all`}
            style={{ width: `${pct}%` }}
          />
        </div>
        {/* WHERE NOW IS.
            Near-white and dark-shadowed, deliberately NOT any of the bar's
            three state colours — green, amber, red — because a marker in one
            of those reads as a property of the progress rather than as the
            clock. It also cannot be the fill's own edge, which is why it
            overhangs. */}
        {elapsed > 0 ? (
          <div
            className="pointer-events-none absolute inset-y-0 w-0.5 bg-ink shadow-[0_0_4px_rgba(0,0,0,0.9)]"
            style={{ left: `${elapsedPct}%`, marginLeft: '-1px' }}
            title={`${elapsedPct}% of the campaign's time has gone`}
          />
        ) : null}
      </div>
      <div className="mt-1 flex items-center justify-between text-[11px] text-ink-dim">
        <span>{pct}% of the commitment shipped</span>
        {/* The comparison stated in words as well as drawn, because the drawn
            version needs the reader to know what the tick means. */}
        <span className={pct >= elapsedPct ? 'text-accent-success' : 'text-amber-300'}>
          {elapsedPct}% of the time gone{pct >= elapsedPct ? ' — ahead' : ' — behind'}
        </span>
      </div>
      {progress.goals.length > 0 && (
        <ul className="mt-4 space-y-2">
          {progress.goals.map((g) => (
            <li key={g.id} className="flex items-center justify-between text-sm">
              <span className="truncate text-ink">{g.name}</span>
              <span className={`ml-3 text-xs ${g.hit_now ? 'text-accent-success' : 'text-ink-muted'}`}>
                {g.progress}/{g.target} · {g.hit_now ? 'hit ✓' : 'in progress'}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

interface GoalsTabProps {
  adventureId: string;
  canEdit: boolean;
  goals: AdventureGoal[];
  commitment: AdventureCommitmentTicket[];
  /** Ids of goals whose condition is satisfied right now, from live progress. */
  hitNowIds?: Set<string>;
}

function GoalsTab({ adventureId, canEdit, goals, commitment, hitNowIds }: GoalsTabProps) {
  const qc = useQueryClient();
  const [kind, setKind] = useState<'tickets' | 'count'>('tickets');
  const [name, setName] = useState('');
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [hitWhen, setHitWhen] = useState<'all_closed' | 'any_one_closed'>('all_closed');
  const [metricEventType, setMetricEventType] = useState('');
  const [metricTarget, setMetricTarget] = useState<number>(1);
  const [expandedGoalId, setExpandedGoalId] = useState<string | null>(null);

  // Live Jira sprint tickets for the goal picker. The adventure's
  // commitment is a frozen snapshot (set at start); the picker shows
  // the CURRENT sprint tickets so the operator can pick from tickets
  // added mid-sprint. Falls back to the frozen commitment when the
  // adventure has no Jira link / the fetch fails.
  const sprintTickets = useQuery({
    queryKey: ['adventure-sprint-tickets', adventureId],
    queryFn: () => adventuresApi.getSprintTickets(adventureId),
  });
  const pickerTickets = (sprintTickets.data?.tickets?.length ? sprintTickets.data.tickets : commitment);

  const create = useMutation({
    mutationFn: () => {
      if (kind === 'tickets') {
        const ticket_keys = selectedKeys;
        return adventuresApi.createGoal(adventureId, {
          kind: 'tickets',
          name,
          ticket_keys,
          hit_when: hitWhen,
        });
      } else {
        return adventuresApi.createGoal(adventureId, {
          kind: 'count',
          name,
          metric_event_type: metricEventType,
          metric_target: metricTarget,
        });
      }
    },
    onSuccess: () => {
      // Reset the form, leave the kind/name alone so the
      // operator can author multiple similar goals quickly.
      setName('');
      setSelectedKeys([]);
      setMetricEventType('');
      setMetricTarget(1);
      // Refetch the adventure so the new goal appears in the list.
      void qc.invalidateQueries({ queryKey: ['admin-adventure', adventureId] });
    },
  });

  const apiError = create.error instanceof ApiError ? create.error : null;
  const validationIssues = apiError && apiError.body && typeof apiError.body === 'object' && 'issues' in apiError.body
    ? (apiError.body.issues as Array<{ path: string[]; message: string }>)
    : undefined;

  const canSubmit = canEdit
    && name.length > 0
    && (kind === 'tickets'
      ? selectedKeys.length > 0
      : metricEventType.length > 0 && metricTarget > 0);

  return (
    <div className="space-y-6">
      {/* Goals list */}
      <div className="bg-bg-elev border border-border rounded">
        {goals.length === 0 && (
          <div className="px-4 py-6 text-center text-sm text-ink-muted">
            No goals yet. Add a Shape A (ticket bundle) or Shape B (count metric) below.
          </div>
        )}
        <ul className="divide-y divide-border">
          {goals.map((goal, idx) => (
            <GoalRow
              key={goal.id}
              goal={goal}
              position={idx}
              hitNow={hitNowIds?.has(goal.id) ?? false}
              canEdit={canEdit}
              expanded={expandedGoalId === goal.id}
              onToggleExpand={() => setExpandedGoalId(expandedGoalId === goal.id ? null : goal.id)}
              adventureId={adventureId}
              pickerTickets={pickerTickets}
            />
          ))}
        </ul>
      </div>

      {/* Create form */}
      {canEdit && (
        <div className="bg-bg-elev border border-border rounded p-4 space-y-3">
          <h3 className="text-sm font-medium text-ink">Add a goal</h3>

          <div className="flex gap-4 text-sm">
            <label className="flex items-center gap-2">
              <input type="radio" checked={kind === 'tickets'} onChange={() => setKind('tickets')} />
              <span>Shape A · ticket bundle</span>
            </label>
            <label className="flex items-center gap-2">
              <input type="radio" checked={kind === 'count'} onChange={() => setKind('count')} />
              <span>Shape B · count metric</span>
            </label>
          </div>

          <div>
            <label className="block text-xs text-ink-muted mb-1">Name</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={kind === 'tickets' ? 'Land the migration' : 'Ship 10 PRs'}
              className="w-full px-3 py-2 bg-bg-base border border-border rounded text-sm"
            />
          </div>

          {kind === 'tickets' && (
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-xs text-ink-muted">
                  Tickets ({selectedKeys.length} selected)
                </label>
                <div className="flex gap-2 text-xs">
                  <button type="button" onClick={() => setSelectedKeys(pickerTickets.map((t) => t.key))} className="text-brand hover:underline">Select all</button>
                  <span className="text-border">·</span>
                  <button type="button" onClick={() => setSelectedKeys([])} className="text-brand hover:underline">Clear</button>
                </div>
              </div>
              {pickerTickets.length === 0 ? (
                <p className="text-xs text-amber-400">
                  No tickets to pick from — add them to the sprint in Jira, or on the Commitment tab.
                </p>
              ) : (
                <div className="max-h-56 overflow-y-auto border border-border rounded divide-y divide-border">
                  {pickerTickets.map((t) => {
                    const checked = selectedKeys.includes(t.key);
                    return (
                      <label key={t.key} className="flex items-center gap-3 px-3 py-2 text-sm cursor-pointer hover:bg-bg-base">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => setSelectedKeys((prev) => checked ? prev.filter((k) => k !== t.key) : [...prev, t.key])}
                          className="accent-brand"
                        />
                        <span className="font-mono text-xs text-brand shrink-0">{t.key}</span>
                        <span className="truncate text-ink-muted">{t.summary}</span>
                      </label>
                    );
                  })}
                </div>
              )}
              <div>
                <label className="block text-xs text-ink-muted mb-1 mt-3">Hit when</label>
                <select
                  value={hitWhen}
                  onChange={(e) => setHitWhen(e.target.value as 'all_closed' | 'any_one_closed')}
                  className="px-3 py-2 bg-bg-base border border-border rounded text-sm"
                >
                  <option value="all_closed">all tickets closed</option>
                  <option value="any_one_closed">any one ticket closed</option>
                </select>
              </div>
            </div>
          )}

          {kind === 'count' && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-ink-muted mb-1">Event type</label>
                <input
                  value={metricEventType}
                  onChange={(e) => setMetricEventType(e.target.value)}
                  placeholder="e.g. pr.closed"
                  className="w-full px-3 py-2 bg-bg-base border border-border rounded text-sm font-mono"
                />
              </div>
              <div>
                <label className="block text-xs text-ink-muted mb-1">Target count</label>
                <input
                  type="number"
                  min={1}
                  value={metricTarget}
                  onChange={(e) => setMetricTarget(Number(e.target.value))}
                  className="w-full px-3 py-2 bg-bg-base border border-border rounded text-sm"
                />
              </div>
            </div>
          )}

          {apiError && (
            <div className="px-3 py-2 bg-red-900/30 border border-red-700 text-red-200 text-sm rounded">
              {apiError.body && typeof apiError.body === 'object' && 'error' in apiError.body && (apiError.body as { error?: string }).error === 'invalid_body' && validationIssues ? (
                <ul className="list-disc list-inside space-y-1">
                  {validationIssues.map((issue, i) => (
                    <li key={i}><code>{issue.path.join('.')}</code>: {issue.message}</li>
                  ))}
                </ul>
              ) : (
                <span>{(apiError.body as { error?: string } | null)?.error ?? apiError.message}</span>
              )}
            </div>
          )}

          <div className="flex justify-end">
            <button
              type="button"
              disabled={!canSubmit || create.isPending}
              onClick={() => create.mutate()}
              className="px-4 py-2 bg-brand text-white rounded hover:bg-brand-dark transition disabled:opacity-50"
            >
              {create.isPending ? 'Adding…' : 'Add goal'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ---- GoalRow (one goal's display + edit/delete) ----

interface GoalRowProps {
  goal: AdventureGoal;
  position: number;
  /** True when this goal's condition is satisfied right now. */
  hitNow: boolean;
  canEdit: boolean;
  expanded: boolean;
  onToggleExpand: () => void;
  adventureId: string;
  pickerTickets: AdventureCommitmentTicket[];
}

function GoalRow({ goal, position, hitNow, canEdit, expanded, onToggleExpand, adventureId, pickerTickets }: GoalRowProps) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState(goal.name);
  const [editPosition, setEditPosition] = useState(goal.position);
  const [editTicketKeys, setEditTicketKeys] = useState<string[]>(goal.ticket_keys);
  const [editHitWhen, setEditHitWhen] = useState<AdventureGoal['hit_when']>(goal.hit_when);
  const [editMetricType, setEditMetricType] = useState(goal.metric_event_type ?? '');
  const [editMetricTarget, setEditMetricTarget] = useState<number>(goal.metric_target ?? 1);

  useEffect(() => {
    if (!editing) {
      setEditName(goal.name);
      setEditPosition(goal.position);
      setEditTicketKeys(goal.ticket_keys);
      setEditHitWhen(goal.hit_when);
      setEditMetricType(goal.metric_event_type ?? '');
      setEditMetricTarget(goal.metric_target ?? 1);
    }
  }, [goal.name, goal.position, goal.ticket_keys, goal.hit_when, goal.metric_event_type, goal.metric_target, editing]);

  const update = useMutation({
    mutationFn: () => adventuresApi.updateGoal(adventureId, goal.id, {
      name: editName,
      position: editPosition,
      ...(goal.kind === 'tickets'
        ? { ticket_keys: editTicketKeys, hit_when: editHitWhen }
        : { metric_event_type: editMetricType, metric_target: editMetricTarget }),
    }),
    onSuccess: () => setEditing(false),
  });

  const del = useMutation({
    mutationFn: () => adventuresApi.deleteGoal(adventureId, goal.id),
    onSuccess: () => onToggleExpand(),
  });

  const isTickets = goal.kind === 'tickets';
  const targetCount = isTickets ? goal.ticket_keys.length : (goal.metric_target ?? 0);

  return (
    <li
      className={
        hitNow
          ? // Same treatment the live feed gives a finished thing: a gold
            // wash at 20% with a gold rule down the left. One colour meaning
            // "this is done" across the product, rather than a second one
            // invented for this page.
            'px-4 py-3 bg-[rgba(251,191,36,0.20)] border-l-2 border-amber-400/70'
          : 'px-4 py-3'
      }
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xs text-ink-muted font-mono">#{position}</span>
            {isTickets ? (
              <span className="px-1.5 py-0.5 text-xs rounded bg-blue-900/30 text-blue-200 border border-blue-700/40">
                Shape A · tickets
              </span>
            ) : (
              <span className="px-1.5 py-0.5 text-xs rounded bg-purple-900/30 text-purple-200 border border-purple-700/40">
                Shape B · count
              </span>
            )}
            <span className="font-medium text-ink truncate">{goal.name}</span>
          </div>
          {isTickets && (
            <div className="text-xs text-ink-muted">
              {goal.ticket_keys.length} ticket{goal.ticket_keys.length === 1 ? '' : 's'} · hit when <span className="font-mono">{goal.hit_when === 'all_closed' ? 'all closed' : 'any closed'}</span>
            </div>
          )}
          {!isTickets && (
            <div className="text-xs text-ink-muted">
              target: {goal.metric_target} × <span className="font-mono">{goal.metric_event_type}</span>
            </div>
          )}
          {expanded && (
            <details open className="mt-2">
              <summary className="text-xs text-ink-muted cursor-pointer">Details</summary>
              <div className="mt-2 text-xs font-mono space-y-1 text-ink-muted">
                <div>id: <code>{goal.id}</code></div>
                <div>status: {goal.status}</div>
                {isTickets && (
                  <ul className="ml-4 list-disc">
                    {goal.ticket_keys.map((k) => <li key={k}>{k}</li>)}
                  </ul>
                )}
              </div>
            </details>
          )}
        </div>
        {canEdit && (
          <div className="flex gap-2 text-xs">
            <button
              type="button"
              onClick={onToggleExpand}
              className="px-2 py-1 text-ink-muted hover:text-ink"
            >
              {expanded ? 'Hide' : 'Show'}
            </button>
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="px-2 py-1 text-brand hover:underline"
            >
              Edit
            </button>
            <button
              type="button"
              onClick={() => {
                if (window.confirm(`Delete goal "${goal.name}"?`)) del.mutate();
              }}
              disabled={del.isPending}
              className="px-2 py-1 text-red-300 hover:underline disabled:opacity-50"
            >
              Delete
            </button>
          </div>
        )}
      </div>

      {editing && (
        <div className="mt-3 p-3 bg-bg-base border border-border rounded space-y-2">
          <div className="grid grid-cols-3 gap-2">
            <div className="col-span-2">
              <label className="block text-xs text-ink-muted mb-1">Name</label>
              <input
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                className="w-full px-2 py-1 bg-bg-elev border border-border rounded text-sm"
              />
            </div>
            <div>
              <label className="block text-xs text-ink-muted mb-1">Position</label>
              <input
                type="number"
                value={editPosition}
                onChange={(e) => setEditPosition(Number(e.target.value))}
                className="w-full px-2 py-1 bg-bg-elev border border-border rounded text-sm"
              />
            </div>
          </div>

          {isTickets ? (
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-xs text-ink-muted">
                  Tickets ({editTicketKeys.length} selected)
                </label>
                <div className="flex gap-2 text-xs">
                  <button type="button" onClick={() => setEditTicketKeys(pickerTickets.map((t) => t.key))} className="text-brand hover:underline">Select all</button>
                  <span className="text-border">·</span>
                  <button type="button" onClick={() => setEditTicketKeys([])} className="text-brand hover:underline">Clear</button>
                </div>
              </div>
              {pickerTickets.length === 0 ? (
                <p className="text-xs text-amber-400">No tickets to pick from — add them to the sprint in Jira, or on the Commitment tab.</p>
              ) : (
                <div className="max-h-56 overflow-y-auto border border-border rounded divide-y divide-border">
                  {pickerTickets.map((t) => {
                    const checked = editTicketKeys.includes(t.key);
                    return (
                      <label key={t.key} className="flex items-center gap-3 px-3 py-2 text-sm cursor-pointer hover:bg-bg-base">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => setEditTicketKeys((prev) => checked ? prev.filter((k) => k !== t.key) : [...prev, t.key])}
                          className="accent-brand"
                        />
                        <span className="font-mono text-xs text-brand shrink-0">{t.key}</span>
                        <span className="truncate text-ink-muted">{t.summary}</span>
                      </label>
                    );
                  })}
                </div>
              )}
              <div className="mt-2">
                <label className="block text-xs text-ink-muted mb-1">Hit when</label>
                <select
                  value={editHitWhen}
                  onChange={(e) => setEditHitWhen(e.target.value as AdventureGoal['hit_when'])}
                  className="px-3 py-2 bg-bg-elev border border-border rounded text-sm"
                >
                  <option value="all_closed">all tickets closed</option>
                  <option value="any_one_closed">any one ticket closed</option>
                </select>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs text-ink-muted mb-1">Event type</label>
                <input
                  value={editMetricType}
                  onChange={(e) => setEditMetricType(e.target.value)}
                  placeholder="e.g. pr.closed"
                  className="w-full px-2 py-1 bg-bg-elev border border-border rounded text-sm font-mono"
                />
              </div>
              <div>
                <label className="block text-xs text-ink-muted mb-1">Target count</label>
                <input
                  type="number"
                  min={1}
                  value={editMetricTarget}
                  onChange={(e) => setEditMetricTarget(Number(e.target.value))}
                  className="w-full px-2 py-1 bg-bg-elev border border-border rounded text-sm"
                />
              </div>
            </div>
          )}
          <div className="flex justify-end gap-2 text-xs">
            <button
              type="button"
              onClick={() => setEditing(false)}
              className="px-3 py-1 text-ink-muted hover:text-ink"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => update.mutate()}
              disabled={update.isPending}
              className="px-3 py-1 bg-brand text-white rounded hover:bg-brand-dark transition disabled:opacity-50"
            >
              {update.isPending ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      )}
    </li>
  );
}

// ---- CommitmentTab (Phase 1 form, unchanged behavior) ----

interface CommitmentTabProps {
  adventure: AdventureWithGoals;
  isDraft: boolean;
  onSaved: () => void;
}

function CommitmentTab({ adventure: adv, isDraft, onSaved }: CommitmentTabProps) {
  const [name, setName] = useState(adv.name);
  const [jiraSprintId, setJiraSprintId] = useState(adv.jira_sprint_id ?? '');
  const [startsAt, setStartsAt] = useState(toLocalInput(adv.starts_at));
  const [endsAt, setEndsAt] = useState(toLocalInput(adv.ends_at));
  const [graceDays, setGraceDays] = useState(adv.completion_grace_days);
  const [commitmentText, setCommitmentText] = useState('');

  useEffect(() => {
    setName(adv.name);
    setJiraSprintId(adv.jira_sprint_id ?? '');
    setStartsAt(toLocalInput(adv.starts_at));
    setEndsAt(toLocalInput(adv.ends_at));
    setGraceDays(adv.completion_grace_days);
    setCommitmentText(
      adv.commitment.map((t) => `${t.key} | ${t.type} | ${t.summary}`).join('\n'),
    );
  }, [adv]);

  const update = useMutation({
    mutationFn: (patch: Parameters<typeof adventuresApi.update>[1]) =>
      adventuresApi.update(adv.id, patch),
    onSuccess: () => onSaved(),
  });

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const commitment = commitmentText
      .split('\n')
      .map((line) => line.trim())
      .filter((line) => line.length > 0)
      .map((line) => {
        const parts = line.split('|').map((p) => p.trim());
        const [key = '', type = 'task', summary = ''] = parts;
        return {
          key,
          type,
          summary: summary || type,
          points_at_snapshot: null,
          actor_external: null,
        };
      });
    update.mutate({
      name,
      starts_at: localToIso(startsAt),
      ends_at: localToIso(endsAt),
      jira_sprint_id: jiraSprintId || null,
      completion_grace_days: graceDays,
      commitment,
    });
  };

  const apiError = update.error instanceof ApiError ? update.error : null;

  return (
    <form onSubmit={submit} className="space-y-6 bg-bg-elev border border-border rounded p-6">
      <fieldset disabled={!isDraft} className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-ink mb-1">Name</label>
          <input
            maxLength={200}
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full px-3 py-2 bg-bg-base border border-border rounded disabled:opacity-60"
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-ink mb-1">Starts at</label>
            <input
              type="datetime-local"
              value={startsAt}
              onChange={(e) => setStartsAt(e.target.value)}
              className="w-full px-3 py-2 bg-bg-base border border-border rounded disabled:opacity-60"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-ink mb-1">Ends at</label>
            <input
              type="datetime-local"
              value={endsAt}
              onChange={(e) => setEndsAt(e.target.value)}
              className="w-full px-3 py-2 bg-bg-base border border-border rounded disabled:opacity-60"
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-ink mb-1">Jira sprint id</label>
            <input
              value={jiraSprintId ?? ''}
              onChange={(e) => setJiraSprintId(e.target.value)}
              placeholder="optional"
              className="w-full px-3 py-2 bg-bg-base border border-border rounded disabled:opacity-60"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-ink mb-1">
              Completion grace days
              <span className="ml-2 text-xs text-ink-muted font-normal">(0 = strict)</span>
            </label>
            <input
              type="number"
              min={0}
              max={7}
              value={graceDays}
              onChange={(e) => setGraceDays(Number(e.target.value))}
              className="w-full px-3 py-2 bg-bg-base border border-border rounded disabled:opacity-60"
            />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-ink mb-1">
            Commitment
            <span className="ml-2 text-xs text-ink-muted font-normal">
              one ticket per line: <code>KEY | TYPE | SUMMARY</code>
            </span>
          </label>
          <textarea
            value={commitmentText}
            onChange={(e) => setCommitmentText(e.target.value)}
            rows={8}
            className="w-full px-3 py-2 bg-bg-base border border-border rounded font-mono text-sm disabled:opacity-60"
          />
        </div>
      </fieldset>

      {apiError && (
        <div className="px-3 py-2 bg-red-900/30 border border-red-700 text-red-200 text-sm rounded">
          {(apiError.body as { error?: string } | null)?.error ?? apiError.message}
          {(apiError.body as { message?: string } | null)?.message && (
            <span className="block text-xs mt-1">{(apiError.body as { message?: string }).message}</span>
          )}
        </div>
      )}

      {isDraft && (
        <div className="flex items-center justify-end gap-3">
          {update.isSuccess && <span className="text-xs text-emerald-400">Saved</span>}
          <button
            type="submit"
            disabled={update.isPending || !name}
            className="px-4 py-2 bg-brand text-white rounded hover:bg-brand-dark transition disabled:opacity-50"
          >
            {update.isPending ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      )}
    </form>
  );
}

// ---- MetadataTab ----

interface MetadataTabProps {
  adventure: AdventureWithGoals;
  onArchive: () => Promise<void>;
  onHardDelete: () => Promise<void>;
}

function MetadataTab({ adventure: adv, onArchive, onHardDelete }: MetadataTabProps) {
  const isDraft = adv.status === 'draft';
  const isActive = adv.status === 'active';
  // v0.8.9: hard delete is gated on the api side to refuse only
  // for 'active' adventures. Drafts/closed/archived all clear.
  const isDeletable = !isActive;
  return (
    <div className="bg-bg-elev border border-border rounded p-6 space-y-4 text-sm">
      <div className="grid grid-cols-2 gap-x-6 gap-y-3">
        <div>
          <div className="text-xs text-ink-muted">id</div>
          <div className="font-mono text-xs">{adv.id}</div>
        </div>
        <div>
          <div className="text-xs text-ink-muted">status</div>
          <div><StatusBadge status={adv.status} /></div>
        </div>
        <div>
          <div className="text-xs text-ink-muted">created_by</div>
          <div className="font-mono text-xs">{adv.created_by}</div>
        </div>
        <div>
          <div className="text-xs text-ink-muted">created_at</div>
          <div className="text-xs">{new Date(adv.created_at).toLocaleString()}</div>
        </div>
        <div>
          <div className="text-xs text-ink-muted">commitment_hash</div>
          <div className="font-mono text-xs">{adv.commitment_hash ?? 'null (draft)'}</div>
        </div>
        <div>
          <div className="text-xs text-ink-muted">jira_sprint_id</div>
          <div className="font-mono text-xs">{adv.jira_sprint_id ?? '—'}</div>
        </div>
        <div>
          <div className="text-xs text-ink-muted">completion_grace_days</div>
          <div>{adv.completion_grace_days}</div>
        </div>
        <div>
          <div className="text-xs text-ink-muted">commitment count</div>
          <div>{adv.commitment.length}</div>
        </div>
      </div>

      <div className="border-t border-border pt-4 flex flex-wrap gap-3">
        {isDraft && (
          <button
            type="button"
            onClick={onArchive}
            className="px-3 py-1.5 text-sm text-red-300 border border-red-700/40 rounded hover:bg-red-900/20"
          >
            Archive this adventure
          </button>
        )}
        {isActive && (
          <div className="text-xs text-ink-muted">
            Cannot archive an active adventure. Close it first (Phase 2-3).
          </div>
        )}
        {isDeletable && (
          // v0.8.9: hard delete. Available on draft, closed,
          // and archived adventures. Active is the only status
          // that refuses (api also enforces this). Cascades to
          // goals + bonuses; audit_log row stays so the
          // deletion is recorded even after the row is gone.
          <button
            type="button"
            onClick={onHardDelete}
            className="px-3 py-1.5 text-sm text-red-300 border border-red-700/60 bg-red-950/30 rounded hover:bg-red-900/40"
          >
            Permanently delete this adventure
          </button>
        )}
      </div>

      <div className="text-xs text-ink-muted">
        Lifecycle controls (Start / End / Bonus distribution) land in Phase 2-3.
      </div>
    </div>
  );
}

// ---- SprintTab (v0.8.9: live Jira sprint checklist) ----
//
// Renders every ticket currently in the linked Jira sprint.
// Operator toggles checkboxes to reshape the commitment
// mid-flight and clicks Save to apply. Server validates that
// every checked key still exists in the live sprint and returns
// the diff so the UI can confirm what changed.
//
// Effects (decided by the operator):
//   - Goal ticket_keys: NOT auto-mutated. The operator manages
//     those separately on the Goals tab.
//   - Boss HP pools: NOT touched. Bosses are independent.
//
// Refuses to render for adventures without a jira_sprint_id
// (no live sprint to compare against) or without an enabled
// Jira ingestor (sprint-tickets endpoint returns empty); in
// those cases the operator is shown a message pointing them at
// the Commitment tab to author manually.

interface SprintTabProps {
  adventure: AdventureWithGoals;
  onSaved: () => void;
}

function SprintTab({ adventure: adv, onSaved }: SprintTabProps) {
  const qc = useQueryClient();
  // Live sprint from Jira (re-fetched every 60s; cheap and
  // catches Jira-side edits that the operator wants to mirror).
  const sprintQuery = useQuery({
    queryKey: ['admin-adventure-sprint', adv.id],
    queryFn: () => adventuresApi.sprintTickets(adv.id),
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
  // Local editable set of checked keys. Initialised from the
  // frozen commitment on mount; user edits are local until Save.
  const [checked, setChecked] = useState<Set<string>>(
    () => new Set(adv.commitment.map((t) => t.key)),
  );
  // When the adventure itself re-fetches (e.g. after Save
  // invalidates the parent query), resync the checked set to
  // the new server-side state. We only overwrite the local set
  // when the user is NOT mid-edit (no dirty flag) so an
  // in-progress selection doesn't get clobbered by a refetch.
  const [dirty, setDirty] = useState(false);
  useEffect(() => {
    if (dirty) return;
    setChecked(new Set(adv.commitment.map((t) => t.key)));
  }, [adv.commitment, dirty]);
  const [savedDiff, setSavedDiff] = useState<{ added: string[]; removed: string[] } | null>(null);

  const update = useMutation({
    mutationFn: (keys: string[]) => adventuresApi.updateCommitment(adv.id, keys),
    onSuccess: (res) => {
      setSavedDiff({ added: res.diff.added, removed: res.diff.removed });
      setDirty(false);
      onSaved();
    },
  });

  const sprintTickets = sprintQuery.data?.tickets ?? [];
  const knownSprintKeys = new Set(sprintTickets.map((t) => t.key));
  // Union of sprint + committed so the operator sees any
  // commitment rows that aren't in the current Jira sprint (they
  // get surfaced as 'orphan' rows below the sprint list).
  const orphanKeys = Array.from(
    new Set(adv.commitment.map((t) => t.key).filter((k) => !knownSprintKeys.has(k))),
  );

  const toggle = (key: string) => {
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
    setDirty(true);
    setSavedDiff(null);
  };
  const selectAll = () => {
    setChecked(new Set(sprintTickets.map((t) => t.key)));
    setDirty(true);
    setSavedDiff(null);
  };
  const deselectAll = () => {
    setChecked(new Set());
    setDirty(true);
    setSavedDiff(null);
  };

  const newKeys = Array.from(checked);
  const currentKeys = adv.commitment.map((t) => t.key);
  const added = newKeys.filter((k) => !currentKeys.includes(k));
  const removed = currentKeys.filter((k) => !checked.has(k));
  const hasChanges = added.length > 0 || removed.length > 0;

  const save = () => {
    if (!window.confirm(
      `Reshape commitment?\n` +
      `+${added.length} added, -${removed.length} removed (${newKeys.length} total).\n` +
      `Goals + bosses are not affected.`,
    )) return;
    update.mutate(newKeys);
  };

  // Disabled when Jira isn't reachable, no sprint id, or the
  // user hasn't touched anything.
  const disabledReason = !adv.jira_sprint_id
    ? 'This adventure has no jira_sprint_id — author the commitment manually on the Commitment tab.'
    : sprintQuery.isError
      ? 'Could not reach Jira. The saved commitment still shows below.'
      : null;

  return (
    <div className="space-y-4">
      <div className="bg-bg-elev border border-border rounded p-6">
        <div className="flex items-baseline justify-between mb-3">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-ink-muted">
            Sprint Tickets
          </h2>
          <span className="text-xs text-ink-muted">
            {sprintTickets.length > 0
              ? `${sprintTickets.length} in Jira sprint · ${checked.size} selected`
              : 'No live sprint data'}
          </span>
        </div>
        {disabledReason ? (
          <p className="text-sm text-ink-muted">{disabledReason}</p>
        ) : sprintQuery.isLoading ? (
          <p className="text-sm text-ink-muted">Loading tickets…</p>
        ) : sprintTickets.length === 0 ? (
          <p className="text-sm text-ink-muted">
            The Jira sprint has no tickets, or the ingestor returned no data.
          </p>
        ) : (
          <>
            <div className="flex items-center gap-2 mb-3 text-xs">
              <button type="button" onClick={selectAll} className="px-2 py-1 border border-border rounded hover:bg-bg-base">
                Select all
              </button>
              <button type="button" onClick={deselectAll} className="px-2 py-1 border border-border rounded hover:bg-bg-base">
                Deselect all
              </button>
              {dirty && (
                <span className={hasChanges ? 'text-amber-300 ml-auto' : 'text-ink-muted ml-auto'}>
                  {hasChanges ? 'Unsaved changes' : 'No changes'}
                </span>
              )}
            </div>
            <ul className="divide-y divide-border max-h-[60vh] overflow-y-auto">
              {sprintTickets.map((t) => (
                <li key={t.key} className="py-1.5 flex items-start gap-3">
                  <input
                    type="checkbox"
                    id={`sprint-${t.key}`}
                    checked={checked.has(t.key)}
                    onChange={() => toggle(t.key)}
                    className="mt-0.5 h-4 w-4 rounded border-white/20 bg-bg-panel"
                  />
                  <label htmlFor={`sprint-${t.key}`} className="flex-1 cursor-pointer">
                    <span className="font-mono text-xs text-ink">{t.key}</span>
                    <span className="ml-2 text-sm text-ink">{t.summary}</span>
                    {t.points_at_snapshot != null && (
                      <span className="ml-2 text-xs text-ink-muted">({t.points_at_snapshot} pts)</span>
                    )}
                  </label>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>

      {orphanKeys.length > 0 && (
        // Tickets in the current commitment that are NOT in the
        // current Jira sprint. The server will reject them on
        // Save (with a 422 listing them), so we surface them
        // here so the operator knows they'll be dropped.
        <div className="bg-bg-elev border border-amber-500/30 rounded p-4">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-amber-300 mb-2">
            In commitment but not in current sprint ({orphanKeys.length})
          </h3>
          <p className="text-xs text-ink-muted mb-2">
            These tickets were in the original commitment but are no longer in the Jira sprint. Saving will drop them.
          </p>
          <ul className="text-xs space-y-0.5">
            {orphanKeys.map((k) => (
              <li key={k} className="font-mono text-ink">{k}</li>
            ))}
          </ul>
        </div>
      )}

      {dirty && !disabledReason && (
        <div className="bg-bg-elev border border-border rounded p-4 flex items-center justify-between gap-4">
          <div className="text-sm">
            {hasChanges ? (
              <>
                <div>
                  <span className="text-emerald-300">+{added.length}</span> added
                  {' · '}
                  <span className="text-red-300">−{removed.length}</span> removed
                  {' · '}
                  <span className="text-ink-muted">{newKeys.length} total</span>
                </div>
                {removed.length > 0 && (
                  <div className="text-xs text-ink-muted mt-1">
                    Removing: {removed.slice(0, 8).join(', ')}{removed.length > 8 ? `, +${removed.length - 8} more` : ''}
                  </div>
                )}
              </>
            ) : (
              <span className="text-ink-muted">No changes to save.</span>
            )}
          </div>
          <button
            type="button"
            onClick={save}
            disabled={update.isPending || !hasChanges}
            className="px-4 py-2 bg-brand text-white rounded hover:bg-brand/90 disabled:opacity-60"
          >
            {update.isPending ? 'Saving…' : 'Save commitment'}
          </button>
        </div>
      )}

      {savedDiff && (
        <div className="bg-emerald-950/30 border border-emerald-700/40 rounded p-3 text-sm">
          Commitment updated.
          {' +'}{savedDiff.added.length}{' / −'}{savedDiff.removed.length}{' · '}
          {savedDiff.added.length === 0 && savedDiff.removed.length === 0 ? 'no change' : 'applied'}
        </div>
      )}

      {update.isError && (
        <div className="bg-red-950/30 border border-red-700/40 rounded p-3 text-sm text-red-200">
          Save failed: {(update.error as Error)?.message ?? 'unknown error'}
        </div>
      )}
    </div>
  );
}
